// src/components/dashboard/ActivityDateChart.jsx
import React, { useState, useMemo } from 'react';
import { COLORS } from '../../constants';
import { PAGE, Button } from '../ui';
import { getRowDate, normaliseDate } from '../../utils/statistics';

export default function ActivityDateChart({
  data = [],
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  allDates = [],
}) {
  const [viewMode, setViewMode] = useState('daily');
  const [hoveredBar, setHoveredBar] = useState(null);

  const parseLocalDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const toISODate = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatShortDate = (dateStr) => {
    return parseLocalDate(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getWeekStart = (dateStr) => {
    const d = parseLocalDate(dateStr);
    const weekStart = new Date(d);
    weekStart.setDate(d.getDate() - d.getDay());
    return toISODate(weekStart);
  };

  const getWeekEnd = (weekStartStr) => {
    const weekStart = parseLocalDate(weekStartStr);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return toISODate(weekEnd);
  };

  // Enhanced date extraction from various formats including scan codes
  const extractDateFromValue = (value) => {
    if (!value) return null;
    
    const str = String(value).trim();
    if (!str) return null;
    
    // 1. Check for YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      return str;
    }
    
    // 2. Check for MM/DD/YYYY or M/D/YYYY
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str)) {
      const parts = str.split('/');
      return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
    }
    
    // 3. Check for DD/MM/YYYY (when day > 12)
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(str) && parseInt(str.split('/')[0]) > 12) {
      const parts = str.split('/');
      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
    
    // 4. Check for scan codes like DTM293503NS, DTM293503NR
    // Format: DTM + YYMMDD + optional suffix
    if (/^DTM\d{6}[A-Z]*$/.test(str)) {
      const code = str.substring(3, 9); // Get 6 digits after DTM
      const year = parseInt(code.substring(0, 2));
      const month = parseInt(code.substring(2, 4));
      const day = parseInt(code.substring(4, 6));
      
      // Validate reasonable date (2020-2030 range)
      if (year >= 20 && year <= 30 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const fullYear = 2000 + year;
        return `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
    
    // 5. Check for timestamp with prefix like SCAN20240616 or similar
    if (/^[A-Z]*\d{8}[A-Z]*$/.test(str)) {
      const digits = str.replace(/[^0-9]/g, '');
      if (digits.length === 8) {
        const year = parseInt(digits.substring(0, 4));
        const month = parseInt(digits.substring(4, 6));
        const day = parseInt(digits.substring(6, 8));
        if (year >= 2020 && year <= 2030 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
      }
    }
    
    // 6. Check for YYMMDD format (6 digits)
    if (/^\d{6}$/.test(str)) {
      const year = parseInt(str.substring(0, 2));
      const month = parseInt(str.substring(2, 4));
      const day = parseInt(str.substring(4, 6));
      if (year >= 20 && year <= 30 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const fullYear = 2000 + year;
        return `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
    
    // 7. Try parsing as timestamp (milliseconds or seconds)
    if (/^\d{10,13}$/.test(str)) {
      const timestamp = parseInt(str);
      const date = new Date(timestamp > 9999999999 ? timestamp : timestamp * 1000);
      if (!isNaN(date) && date.getFullYear() >= 2020) {
        return toISODate(date);
      }
    }
    
    // 8. Try native Date parsing as last resort
    try {
      const date = new Date(str);
      if (!isNaN(date) && date.getFullYear() >= 2020 && date.getFullYear() <= 2030) {
        return toISODate(date);
      }
    } catch (e) {}
    
    return null;
  };

  // Check if a value contains a date
  const hasDate = (value) => {
    return extractDateFromValue(value) !== null;
  };

  const chartData = useMemo(() => {
    const dateMap = {};
    let found = false;

    data.forEach((row) => {
      const dateStr = normaliseDate(getRowDate(row));
      if (dateStr) {
        dateMap[dateStr] = (dateMap[dateStr] || 0) + 1;
      }
    });

    if (viewMode === 'weekly') {
      const weekMap = {};

      Object.entries(dateMap).forEach(([date, count]) => {
        const weekStart = getWeekStart(date);
        const weekEnd = getWeekEnd(weekStart);

        if (!weekMap[weekStart]) {
          weekMap[weekStart] = {
            date: weekStart,
            weekStart,
            weekEnd,
            count: 0,
          };
        }

        weekMap[weekStart].count += count;
      });

      return Object.values(weekMap)
        .sort((a, b) => a.weekStart.localeCompare(b.weekStart))
        .map((week) => ({
          ...week,
          label: `${formatShortDate(week.weekStart)} - ${formatShortDate(
            week.weekEnd
          )}`,
        }));
    }

    return Object.entries(dateMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({
        date,
        count,
        label: formatShortDate(date),
      }));
  }, [data, viewMode]);

  const visibleData =
    viewMode === 'weekly' ? chartData.slice(-8) : chartData.slice(-14);

  const maxCount = Math.max(...visibleData.map((d) => d.count), 1);
  const hasActiveFilter = Boolean(dateFrom || dateTo);

  const handleClearFilter = () => {
    onDateFromChange('');
    onDateToChange('');
    setHoveredBar(null);
  };

  const handleBarClick = (d) => {
    if (viewMode === 'weekly') {
      onDateFromChange(d.weekStart);
      onDateToChange(d.weekEnd);
    } else {
      onDateFromChange(d.date);
      onDateToChange(d.date);
    }
  };

  const isSelectedDate = (d) => {
    if (viewMode === 'weekly') {
      return Boolean(
        dateFrom && dateTo && d.weekStart === dateFrom && d.weekEnd === dateTo
      );
    }

    return Boolean(dateFrom && dateTo && d.date === dateFrom && d.date === dateTo);
  };

  const makeTicks = () => {
    const rawTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) =>
      Math.round(maxCount * ratio)
    );

    return Array.from(new Set(rawTicks)).sort((a, b) => a - b);
  };

  const hoveredItem =
    hoveredBar !== null && visibleData[hoveredBar] ? visibleData[hoveredBar] : null;

  const SVG_WIDTH = 980;
  const SVG_HEIGHT = 260;
  const PADDING = { top: 32, right: 24, bottom: 56, left: 52 };
  const plotWidth = SVG_WIDTH - PADDING.left - PADDING.right;
  const plotHeight = SVG_HEIGHT - PADDING.top - PADDING.bottom;
  const barCount = Math.max(visibleData.length, 1);
  const step = plotWidth / barCount;
  const barWidth = Math.max(
    18,
    Math.min(viewMode === 'weekly' ? 64 : 44, step * 0.58)
  );
  const ticks = makeTicks();

  return (
    <div
      style={{
        background: PAGE.colors.surface,
        border: `1px solid ${PAGE.colors.line}`,
        borderRadius: PAGE.radius.card,
        padding: 16,
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'flex-start',
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 15,
              fontWeight: 800,
              color: PAGE.colors.text,
              letterSpacing: '-0.01em',
            }}
          >
            Activity Distribution
          </div>
          <div
            style={{
              fontSize: 11,
              color: PAGE.colors.muted,
              marginTop: 3,
            }}
          >
            Click any bar to filter the dashboard by that date range
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button
            variant={viewMode === 'daily' ? 'outlineActive' : 'outline'}
            size="sm"
            onClick={() => setViewMode('daily')}
            style={{ padding: '5px 11px', fontSize: 12 }}
          >
            Daily
          </Button>

          <Button
            variant={viewMode === 'weekly' ? 'outlineActive' : 'outline'}
            size="sm"
            onClick={() => setViewMode('weekly')}
            style={{ padding: '5px 11px', fontSize: 12 }}
          >
            Weekly
          </Button>
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          border: `1px solid ${PAGE.colors.line}`,
          borderRadius: 14,
          background:
            'linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 1) 100%)',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        {hoveredItem && (
          <div
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 3,
              padding: '8px 10px',
              background: '#111827',
              color: '#ffffff',
              borderRadius: 10,
              boxShadow: '0 10px 24px rgba(15, 23, 42, 0.18)',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 800 }}>
              {hoveredItem.count} Assets
            </div>
            <div style={{ fontSize: 11, opacity: 0.78, marginTop: 2 }}>
              {hoveredItem.label}
            </div>
          </div>
        )}

        {visibleData.length === 0 ? (
          <div
            style={{
              height: 230,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: PAGE.colors.muted,
              fontSize: 13,
            }}
          >
            No timestamp data available
          </div>
        ) : (
          <svg
            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
            width="100%"
            height="250"
            preserveAspectRatio="none"
            role="img"
            aria-label="Activity distribution chart"
            style={{ display: 'block', minWidth: viewMode === 'weekly' ? 760 : 860 }}
          >
            <defs>
              <linearGradient id="activityBar" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#38bdf8" />
                <stop offset="100%" stopColor="#2563eb" />
              </linearGradient>
              <linearGradient id="activityBarSelected" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22c55e" />
                <stop offset="100%" stopColor="#16a34a" />
              </linearGradient>
              <linearGradient id="activityBarHover" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#f97316" />
              </linearGradient>
            </defs>

            <line
              x1={PADDING.left}
              y1={PADDING.top + plotHeight}
              x2={PADDING.left + plotWidth}
              y2={PADDING.top + plotHeight}
              stroke={PAGE.colors.line}
              strokeWidth="1.4"
            />

            <line
              x1={PADDING.left}
              y1={PADDING.top}
              x2={PADDING.left}
              y2={PADDING.top + plotHeight}
              stroke={PAGE.colors.line}
              strokeWidth="1.4"
            />

            {ticks.map((tick) => {
              const y = PADDING.top + plotHeight - (tick / maxCount) * plotHeight;

              return (
                <g key={`tick-${tick}`}>
                  <line
                    x1={PADDING.left}
                    y1={y}
                    x2={PADDING.left + plotWidth}
                    y2={y}
                    stroke={PAGE.colors.line}
                    strokeWidth="1"
                    strokeDasharray={tick === 0 ? '0' : '5 7'}
                    opacity={tick === 0 ? 0.8 : 0.62}
                  />
                  <text
                    x={PADDING.left - 13}
                    y={y + 4}
                    textAnchor="end"
                    fontSize="12"
                    fill={PAGE.colors.muted}
                  >
                    {tick}
                  </text>
                </g>
              );
            })}

            <text
              x={18}
              y={PADDING.top + plotHeight / 2}
              textAnchor="middle"
              fontSize="12"
              fill={PAGE.colors.muted}
              transform={`rotate(-90 18 ${PADDING.top + plotHeight / 2})`}
            >
              Assets
            </text>

            {visibleData.map((d, idx) => {
              const selected = isSelectedDate(d);
              const hovered = hoveredBar === idx;
              const barHeight = Math.max((d.count / maxCount) * plotHeight, 8);
              const x = PADDING.left + idx * step + (step - barWidth) / 2;
              const y = PADDING.top + plotHeight - barHeight;
              const fill = selected
                ? 'url(#activityBarSelected)'
                : hovered
                  ? 'url(#activityBarHover)'
                  : 'url(#activityBar)';

              return (
                <g
                  key={`${d.date}-${idx}`}
                  onClick={() => handleBarClick(d)}
                  onMouseEnter={() => setHoveredBar(idx)}
                  onMouseLeave={() => setHoveredBar(null)}
                  style={{ cursor: 'pointer' }}
                >
                  <rect
                    x={x - 5}
                    y={PADDING.top}
                    width={barWidth + 10}
                    height={plotHeight}
                    fill="transparent"
                  />

                  <rect
                    x={x}
                    y={y}
                    width={barWidth}
                    height={barHeight}
                    rx="8"
                    fill={fill}
                    opacity={hovered || selected ? 1 : 0.88}
                  />

                  <text
                    x={x + barWidth / 2}
                    y={Math.max(y - 8, 16)}
                    textAnchor="middle"
                    fontSize="12"
                    fontWeight="700"
                    fill={selected ? COLORS.success : PAGE.colors.text}
                  >
                    {d.count}
                  </text>

                  <text
                    x={x + barWidth / 2}
                    y={PADDING.top + plotHeight + 24}
                    textAnchor="middle"
                    fontSize={viewMode === 'weekly' ? '10' : '11'}
                    fontWeight={selected ? '800' : '600'}
                    fill={selected ? COLORS.success : PAGE.colors.muted}
                  >
                    {d.label}
                  </text>

                  {selected && (
                    <circle
                      cx={x + barWidth / 2}
                      cy={PADDING.top + plotHeight + 38}
                      r="3"
                      fill={COLORS.success}
                    />
                  )}
                </g>
              );
            })}
          </svg>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginTop: 12,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            fontSize: 12,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ ...PAGE.type.label }}>From</span>

          <input
            type="date"
            value={dateFrom || ''}
            onChange={(e) => onDateFromChange(e.target.value)}
            min={allDates?.[0]}
            max={allDates?.[allDates.length - 1]}
            style={{
              padding: '6px 10px',
              fontSize: 12,
              border: `1px solid ${PAGE.colors.line}`,
              borderRadius: PAGE.radius.input,
              background: PAGE.colors.surface,
              color: PAGE.colors.text,
            }}
          />

          <span style={{ ...PAGE.type.label }}>to</span>

          <input
            type="date"
            value={dateTo || ''}
            onChange={(e) => onDateToChange(e.target.value)}
            min={allDates?.[0]}
            max={allDates?.[allDates.length - 1]}
            style={{
              padding: '6px 10px',
              fontSize: 12,
              border: `1px solid ${PAGE.colors.line}`,
              borderRadius: PAGE.radius.input,
              background: PAGE.colors.surface,
              color: PAGE.colors.text,
            }}
          />

          <Button
            variant="outline"
            size="sm"
            onClick={handleClearFilter}
            disabled={!hasActiveFilter}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              opacity: hasActiveFilter ? 1 : 0.45,
              cursor: hasActiveFilter ? 'pointer' : 'not-allowed',
            }}
          >
            Clear Filter
          </Button>
        </div>

        {hasActiveFilter && (
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: COLORS.success,
              background: `${COLORS.success}15`,
              border: `1px solid ${COLORS.success}30`,
              borderRadius: 999,
              padding: '6px 10px',
            }}
          >
            Filter active
          </div>
        )}
      </div>
    </div>
  );
}
